// src/components/SessionGate/SessionGate.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../../lib/firebase';
import { useAuthUser } from '../../auth/useAuthUser';
import {
	cacheLooksAccepted,
	checkServerAcceptance,
	recordAcceptanceToServer,
	hasSeenWelcome,
	markWelcomeSeen,
} from '../../utils/acceptance';
import { AcceptanceFailure } from '../AcceptanceFailure/AcceptanceFailure';
import {
	SIGNUP_ACCEPTANCE,
	REACCEPTANCE,
	WELCOME_PROMISE,
	TOS_VERSION,
} from '../../constants/DISCLAIMERS';
import styles from './SessionGate.module.scss';

// Gate states:
//   loading         → auth still resolving, or the background server check is
//                     running WITHOUT a satisfied local cache (so we can't yet
//                     safely show the app).
//   verifying       → local cache looks accepted, so we render the app while the
//                     server check runs in the background (no flash). If the
//                     server later says stale/missing, we flip to needs-acceptance.
//   ready           → server-confirmed (or logged out); app is usable.
//   needs-acceptance→ server says missing/stale; blocking re-acceptance modal.
//   check-error     → the server check itself failed; soft error, not a silent
//                     trust-the-cache. Offers retry.
//   accept-error    → the re-acceptance WRITE failed; blocking failure screen.
type GateState =
	| 'loading'
	| 'verifying'
	| 'ready'
	| 'needs-acceptance'
	| 'needs-welcome'
	| 'check-error'
	| 'accept-error';

const SessionGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { user, loading } = useAuthUser();
	const navigate = useNavigate();
	const [state, setState] = useState<GateState>('loading');

	// Re-acceptance checkbox state
	const [ageOk, setAgeOk] = useState(false);
	const [termsOk, setTermsOk] = useState(false);

	// Failure-screen state for a failed re-acceptance WRITE.
	const [acceptWriteError, setAcceptWriteError] = useState<string | null>(
		null,
	);
	const [busy, setBusy] = useState(false);

	// ── Resolve gate state from auth + SERVER acceptance ──
	// Server is authoritative. Local cache only decides whether we can show the
	// app WHILE verifying (to avoid a gate flash), never whether the user is in.
	const resolve = useCallback(async () => {
		if (loading) {
			setState('loading');
			return;
		}
		if (!user) {
			// Logged-out users aren't gated here (auth routing handles them).
			setState('ready');
			return;
		}

		// Optimistic: if the local cache looks current, render the app while we
		// verify in the background. Otherwise hold on the loader until the
		// server answers — never show the app on an unsatisfied cache.
		const optimistic = cacheLooksAccepted(user.uid);
		setState(optimistic ? 'verifying' : 'loading');

		let serverState: 'current' | 'stale-or-missing';
		try {
			serverState = await checkServerAcceptance(user.uid);
		} catch {
			// The check itself failed. Do NOT fall back to trusting the cache
			// for access — show a soft error with a retry instead.
			setState('check-error');
			return;
		}

		if (serverState === 'stale-or-missing') {
			setState('needs-acceptance');
			return;
		}

		// Server confirms acceptance. Welcome modal is a pure local one-time UX.
		if (!hasSeenWelcome(user.uid)) {
			setState('needs-welcome');
			return;
		}
		setState('ready');
	}, [user, loading]);

	useEffect(() => {
		void resolve();
	}, [resolve]);

	// While a blocking modal/screen is open, lock background scroll.
	useEffect(() => {
		const modalOpen =
			state === 'needs-acceptance' ||
			state === 'needs-welcome' ||
			state === 'accept-error' ||
			state === 'check-error';
		if (!modalOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [state]);

	// ── Handlers ──
	const handleReAccept = async () => {
		if (!user || !ageOk || !termsOk || busy) return;
		setBusy(true);
		try {
			// Blocking: the server must record acceptance before we let the user
			// continue. recordAcceptanceToServer throws on any failed POST.
			await recordAcceptanceToServer(user.uid);
			setAcceptWriteError(null);
			// Re-resolve rather than assuming ready — picks up the welcome step.
			await resolve();
		} catch (err: unknown) {
			setAcceptWriteError(
				err instanceof Error
					? err.message
					: 'Failed to save acceptance.',
			);
			setState('accept-error');
		} finally {
			setBusy(false);
		}
	};

	const handleRetryWrite = async () => {
		// From the write-failure screen: go back to the modal and let them
		// re-submit (boxes are still ticked in state).
		setAcceptWriteError(null);
		await handleReAccept();
	};

	const handleRetryCheck = async () => {
		await resolve();
	};

	const handleDismissWelcome = () => {
		if (!user) return;
		markWelcomeSeen(user.uid);
		void resolve();
	};

	const handleDecline = async () => {
		await signOut(auth);
		// auth state updates → user null → resolve() sets 'ready' (logged out).
		// Redirect to login so declining reads as a deliberate sign-out.
		navigate('/login');
	};

	// ── Render ──
	if (state === 'loading') {
		return <div className={styles.loading}>Loading…</div>;
	}

	// The re-acceptance WRITE failed: blocking failure screen, user stays out.
	if (state === 'accept-error') {
		return (
			<div className={styles.overlay}>
				<div className={styles.modal}>
					<AcceptanceFailure
						provider={
							user?.providerData?.[0]?.providerId === 'google.com'
								? 'google'
								: 'password'
						}
						errorMessage={
							acceptWriteError ?? 'Failed to save acceptance.'
						}
						route='/reaccept'
						busy={busy}
						onRetry={handleRetryWrite}
						onSignOut={handleDecline}
					/>
				</div>
			</div>
		);
	}

	// The server CHECK failed (not the write): soft error with retry. We don't
	// trust the local cache for access when a fresh check was required.
	if (state === 'check-error') {
		return (
			<div className={styles.overlay}>
				<div className={styles.modal}>
					<h2 className={styles.title}>
						Couldn’t verify your account
					</h2>
					<p className={styles.subtitle}>
						We couldn’t reach Nisk to confirm your account is ready.
						Check your connection and try again.
					</p>
					<div className={styles.actions}>
						<button
							className={styles.primaryBtn}
							onClick={() => void handleRetryCheck()}
							disabled={busy}>
							Retry
						</button>
						<button
							className={styles.secondaryBtn}
							onClick={() => void handleDecline()}
							disabled={busy}>
							Sign out
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			{children}

			{/* ── Re-acceptance (blocking) ── */}
			{state === 'needs-acceptance' && (
				<div
					className={styles.overlay}
					role='dialog'
					aria-modal='true'
					aria-label='Updated terms'>
					<div className={styles.modal}>
						<h2 className={styles.title}>
							We've updated our terms
						</h2>
						<p className={styles.subtitle}>
							Our Terms of Service and Disclaimer changed (version{' '}
							{TOS_VERSION}). Please confirm to keep using Nisk.
						</p>

						<div className={styles.checkGroup}>
							<label className={styles.checkRow}>
								<input
									type='checkbox'
									className={styles.checkInput}
									checked={ageOk}
									onChange={(e) => setAgeOk(e.target.checked)}
								/>
								<span
									className={styles.checkBox}
									aria-hidden='true'>
									{ageOk && <CheckIcon />}
								</span>
								<span className={styles.checkText}>
									{SIGNUP_ACCEPTANCE.age}
								</span>
							</label>

							<label className={styles.checkRow}>
								<input
									type='checkbox'
									className={styles.checkInput}
									checked={termsOk}
									onChange={(e) =>
										setTermsOk(e.target.checked)
									}
								/>
								<span
									className={styles.checkBox}
									aria-hidden='true'>
									{termsOk && <CheckIcon />}
								</span>
								<span className={styles.checkText}>
									{
										REACCEPTANCE.terms.split(
											'Terms of Service',
										)[0]
									}
									<Link
										to='/legal/terms'
										target='_blank'
										rel='noopener'
										className={styles.legalLink}>
										Terms of Service
									</Link>
									,{' '}
									<Link
										to='/legal/privacy'
										target='_blank'
										rel='noopener'
										className={styles.legalLink}>
										Privacy Policy
									</Link>
									, and{' '}
									<Link
										to='/legal/disclaimer'
										target='_blank'
										rel='noopener'
										className={styles.legalLink}>
										Disclaimer
									</Link>
									{REACCEPTANCE.terms.split('Disclaimer')[1]}
								</span>
							</label>
						</div>

						<div className={styles.actions}>
							<button
								className={styles.primaryBtn}
								onClick={() => void handleReAccept()}
								disabled={!ageOk || !termsOk || busy}>
								{busy ? 'Saving…' : 'Agree & Continue'}
							</button>
							<button
								className={styles.secondaryBtn}
								onClick={handleDecline}
								disabled={busy}>
								Decline &amp; Sign Out
							</button>
						</div>
					</div>
				</div>
			)}

			{/* ── First-time welcome (dismissible) ── */}
			{state === 'needs-welcome' && (
				<div
					className={styles.overlay}
					role='dialog'
					aria-modal='true'
					aria-label='Welcome'>
					<div className={styles.modal}>
						<h2 className={styles.title}>
							{WELCOME_PROMISE.title}
						</h2>
						{WELCOME_PROMISE.body.map((para, i) => (
							<p
								key={i}
								className={styles.welcomeBody}>
								{para}
							</p>
						))}
						<div className={styles.actions}>
							<button
								className={styles.primaryBtn}
								onClick={handleDismissWelcome}>
								{WELCOME_PROMISE.cta}
							</button>
						</div>
						<p className={styles.fineprint}>
							<Link
								to='/legal/terms'
								target='_blank'
								rel='noopener'
								className={styles.legalLink}>
								Terms
							</Link>
							{' · '}
							<Link
								to='/legal/disclaimer'
								target='_blank'
								rel='noopener'
								className={styles.legalLink}>
								Disclaimer
							</Link>
							{' · '}
							<Link
								to='/legal/privacy'
								target='_blank'
								rel='noopener'
								className={styles.legalLink}>
								Privacy
							</Link>
						</p>
					</div>
				</div>
			)}
		</>
	);
};

const CheckIcon = () => (
	<svg
		viewBox='0 0 14 14'
		fill='none'
		stroke='currentColor'
		strokeWidth='2.2'
		strokeLinecap='round'
		strokeLinejoin='round'>
		<path d='M3 7.5l2.5 2.5L11 4.5' />
	</svg>
);

export default SessionGate;
